// src/developer/DeveloperPanel.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./DeveloperPanel.css";

const DeveloperPanel = () => {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [user, setUser] = useState(null);

  // Tabs: "tables" | "requests" | "reports"
  const [activeTab, setActiveTab] = useState("tables");

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Upload existing-table
  const [tableFile, setTableFile] = useState(null);
  const [tableName, setTableName] = useState("");
  const [tableVersion, setTableVersion] = useState("");
  const [description, setDescription] = useState("");

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  // Existing tables for selected category
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [editingTableId, setEditingTableId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    version: "",
    description: "",
  });

  // Category creation UI
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    slug: "",
    appid: "",
    imageUrl: "", // NEW
  });
  const [creatingCategory, setCreatingCategory] = useState(false);

  // NEW: Selected category image editor
  const [categoryImageUrl, setCategoryImageUrl] = useState("");
  const [savingCategoryImage, setSavingCategoryImage] = useState(false);

  // Optional FIRST TABLE when creating a category
  const [newCategoryFirstTable, setNewCategoryFirstTable] = useState({
    file: null,
    name: "",
    version: "",
    description: "",
  });

  // Steam search (for category creation)
  const [steamSearchQuery, setSteamSearchQuery] = useState("");
  const [steamResults, setSteamResults] = useState([]);
  const [loadingSteam, setLoadingSteam] = useState(false);

  // Game Requests tab state
  const [devRequests, setDevRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsError, setRequestsError] = useState(null);
  const [requestStatusFilter, setRequestStatusFilter] = useState("pending");
  const [requestSearch, setRequestSearch] = useState("");
  const [requestActionLoading, setRequestActionLoading] = useState({});

  // Reports tab state
  const [devReports, setDevReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState(null);
  const [reportsStatusFilter, setReportsStatusFilter] = useState("all");
  const [reportsSearch, setReportsSearch] = useState("");
  const [reportActionLoading, setReportActionLoading] = useState({});

  localStorage.setItem("redirectAfterLogin", window.location.pathname);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setUser(null);
    setUnauthorized(true);
    navigate("/login");
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        console.error("No refresh token found in local storage.");
        throw new Error("No refresh token available.");
      }

      const response = await axios.post(
        "/api/auth/refresh-token",
        {},
        {
          headers: {
            "x-refresh-token": refreshToken,
          },
        }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data || {};

      if (!accessToken) {
        throw new Error("No access token returned from refresh endpoint.");
      }

      localStorage.setItem("token", accessToken);
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }

      return accessToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      handleLogout();
      return null;
    }
  };

  /**
   * Wrapper around axios that:
   *  - sends current auth header
   *  - on 401, tries to refresh token once
   *  - retries the original request with the new token
   */
  const authorizedRequest = async (config, retry = true) => {
    try {
      const res = await axios({
        ...config,
        headers: {
          ...(config.headers || {}),
          ...getAuthHeaders(),
        },
      });
      return res;
    } catch (err) {
      const status = err.response?.status;

      if (status === 401 && retry) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          // refreshAccessToken already handled logout
          throw err;
        }

        // Retry once with new token
        return authorizedRequest(config, false);
      }

      throw err;
    }
  };

  // Map internal status codes to nice human labels
  const REPORT_STATUS_LABELS = {
    green: "Working",
    yellow: "Issues Reported",
    red: "Broken",
  };

  const slugify = (str) =>
    str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  // --- Auth check ---
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await authorizedRequest({
          method: "get",
          url: "/api/auth/dashboard",
        });

        const data = res.data;
        setUser(data);

        if (data.role !== "admin" && data.role !== "developer") {
          setUnauthorized(true);
        }
      } catch (err) {
        console.error("Error checking auth for DeveloperPanel:", err);
        setUnauthorized(true);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Fetch categories ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await authorizedRequest({
          method: "get",
          url: "/api/admin/categories",
        });
        setCategories(res.data || []);
      } catch (err) {
        console.error("Error fetching categories for DeveloperPanel:", err);
        setMessage({
          type: "error",
          text: "Failed to load game list.",
        });
      }
    };

    if (!unauthorized) {
      fetchCategories();
    }
  }, [unauthorized]);

  // --- Fetch tables for a category (by slug, like AdminPanel) ---
  const fetchTablesForCategory = async (category) => {
    if (!category?.slug) {
      setTables([]);
      return;
    }

    try {
      setTablesLoading(true);
      const res = await authorizedRequest({
        method: "get",
        url: `/api/admin/tables/${category.slug}`,
      });
      setTables(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching tables for DeveloperPanel:", err);
      setTables([]);
      setMessage({
        type: "error",
        text: "Failed to load tables for this game.",
      });
    } finally {
      setTablesLoading(false);
    }
  };

  // Refetch tables whenever selectedCategory changes
  useEffect(() => {
    if (!unauthorized && selectedCategory?.slug) {
      fetchTablesForCategory(selectedCategory);
    } else {
      setTables([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unauthorized, selectedCategory]);

  // --- Auto-clear message ---
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(t);
  }, [message]);

  // --- Upload existing table for selected category ---
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "ct") {
      setTableFile(null);
      setMessage({
        type: "error",
        text: "Only .ct files are allowed.",
      });
      return;
    }

    setTableFile(file);
    if (!tableName) setTableName(file.name);
    setMessage({
      type: "success",
      text: "File accepted successfully.",
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "ct") {
      setTableFile(null);
      setMessage({
        type: "error",
        text: "Only .ct files are allowed.",
      });
      return;
    }

    setTableFile(file);
    if (!tableName) setTableName(file.name);
    setMessage({
      type: "success",
      text: "File accepted successfully.",
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCategory) {
      setMessage({ type: "error", text: "Please select a game category." });
      return;
    }
    if (!tableFile || !tableName || !tableVersion || !description.trim()) {
      setMessage({
        type: "error",
        text: "Please provide a file, name, version, and description.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("tableFile", tableFile);
    formData.append("category", selectedCategory._id);
    formData.append("name", tableName);
    formData.append("version", tableVersion);
    formData.append("description", description);

    try {
      setUploading(true);
      await authorizedRequest({
        method: "post",
        url: "/api/admin/upload-table",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setMessage({
        type: "success",
        text: "Table uploaded successfully.",
      });

      setTableFile(null);
      setTableName("");
      setTableVersion("");
      setDescription("");

      if (selectedCategory?.slug) {
        fetchTablesForCategory(selectedCategory);
      }
    } catch (err) {
      console.error("Error uploading table from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error uploading table.",
      });
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const url = selectedCategory?.images?.[0] || "";
    setCategoryImageUrl(url);
  }, [selectedCategory]);

  // --- Category creation logic (no category description) ---

  const handleNewCategoryChange = (field, value) => {
    setNewCategory((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "name" && !prev.slug ? { slug: slugify(value) } : {}),
    }));
  };

  const handleNewCatTableChange = (field, value) => {
    setNewCategoryFirstTable((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewCatTableFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "ct") {
      setNewCategoryFirstTable((prev) => ({ ...prev, file: null }));
      setMessage({
        type: "error",
        text: "Only .ct files are allowed for the first table.",
      });
      return;
    }

    setNewCategoryFirstTable((prev) => ({
      ...prev,
      file,
      name: prev.name || file.name,
    }));
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();

    const name = newCategory.name.trim();
    const slug = (newCategory.slug || slugify(newCategory.name || "")).trim();

    if (!name) {
      setMessage({
        type: "error",
        text: "Category name is required.",
      });
      return;
    }

    // AppID is required in schema – allow raw ID or full Steam URL
    let appId = (newCategory.appid || "").trim();
    const steamAppIdRegex =
      /(?:https?:\/\/)?(?:store\.)?steam(?:community|powered)?\.com\/app\/(\d+)/i;
    const match = appId.match(steamAppIdRegex);
    if (match) {
      appId = match[1];
    }

    if (!appId) {
      setMessage({
        type: "error",
        text: "Steam AppID is required. Use search or paste a valid Steam URL / ID.",
      });
      return;
    }

    const imageUrl = (newCategory.imageUrl || "").trim();

    const payload = {
      name,
      slug,
      appid: appId,
      images: imageUrl ? [imageUrl] : [],
    };

    try {
      setCreatingCategory(true);
      let createdCategory = null;

      // 1) Create the category
      const res = await authorizedRequest({
        method: "post",
        url: "/api/admin/categories",
        data: payload,
      });

      createdCategory = res.data?.category || res.data;

      if (!createdCategory || !createdCategory._id) {
        throw new Error("Category API did not return a valid category.");
      }

      // Update category list & selection
      setCategories((prev) => [...prev, createdCategory]);
      setSelectedCategory(createdCategory);

      // 2) Optionally upload first table
      let tableUploadError = null;

      if (newCategoryFirstTable.file) {
        const fd = new FormData();
        fd.append("tableFile", newCategoryFirstTable.file);
        fd.append("category", createdCategory._id);
        fd.append(
          "name",
          newCategoryFirstTable.name ||
            newCategoryFirstTable.file.name ||
            "New Table"
        );
        fd.append("version", newCategoryFirstTable.version || "1.0");
        fd.append("description", newCategoryFirstTable.description || "");

        try {
          await authorizedRequest({
            method: "post",
            url: "/api/admin/upload-table",
            data: fd,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          // Refresh tables for this category
          fetchTablesForCategory(createdCategory);
        } catch (err2) {
          console.error(
            "Error uploading first table for new category from DeveloperPanel:",
            err2
          );
          tableUploadError =
            err2.response?.data?.message ||
            err2.message ||
            "Unknown error";
        }
      }

      // Reset forms
      setNewCategory({
        name: "",
        slug: "",
        appid: "",
        imageUrl: "", // NEW
      });
      setNewCategoryFirstTable({
        file: null,
        name: "",
        version: "",
        description: "",
      });
      setSteamSearchQuery("");
      setSteamResults([]);
      setShowCategoryForm(false);

      if (tableUploadError) {
        setMessage({
          type: "error",
          text:
            "Category created, but first table upload failed: " +
            tableUploadError,
        });
      } else if (newCategoryFirstTable.file) {
        setMessage({
          type: "success",
          text: "Category and first table created successfully.",
        });
      } else {
        setMessage({
          type: "success",
          text: "Category created successfully.",
        });
      }
    } catch (err) {
      console.error("Error creating category from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error creating category.",
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  // --- Steam search (using correct API: /api/steam/search?query=...) ---

  const handleSteamSearch = async () => {
    const query = steamSearchQuery.trim();
    if (!query) {
      setMessage({
        type: "error",
        text: "Enter a Steam game name to search.",
      });
      return;
    }

    try {
      setLoadingSteam(true);
      setSteamResults([]);

      const response = await authorizedRequest({
        method: "get",
        url: `/api/steam/search?query=${encodeURIComponent(query)}`,
      });

      if (Array.isArray(response.data)) {
        setSteamResults(response.data);
      } else {
        setSteamResults([]);
        setMessage({
          type: "error",
          text: "No Steam games found for that query.",
        });
      }
    } catch (error) {
      console.error("Error searching Steam games:", error);
      setSteamResults([]);
      setMessage({
        type: "error",
        text: "Error searching Steam. Please try again.",
      });
    } finally {
      setLoadingSteam(false);
    }
  };

  const handleSteamResultClick = (result) => {
    const title = result.name || result.title || "";
    let appId = "";

    // Try to get appid directly if backend sends it
    if (result.appid) {
      appId = String(result.appid);
    } else if (result.url) {
      const steamAppIdRegex =
        /(?:https?:\/\/)?(?:store\.)?steam(?:community|powered)?\.com\/app\/(\d+)/i;
      const match = result.url.match(steamAppIdRegex);
      if (match) {
        appId = match[1];
      }
    }

    setNewCategory((prev) => ({
      ...prev,
      name: title || prev.name,
      slug: prev.slug || (title ? slugify(title) : prev.slug),
      appid: appId || prev.appid,
    }));
  };

  // --- Table management (edit/delete/download) ---

  const startEditTable = (table) => {
    setEditingTableId(table._id);
    setEditForm({
      name: table.name || "",
      version: table.version || "",
      description: table.description || "",
    });
  };

  const cancelEditTable = () => {
    setEditingTableId(null);
    setEditForm({
      name: "",
      version: "",
      description: "",
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateTable = async (tableId) => {
    if (!editForm.name.trim() || !editForm.version.trim()) {
      setMessage({
        type: "error",
        text: "Name and version are required for updates.",
      });
      return;
    }

    try {
      await authorizedRequest({
        method: "put",
        url: `/api/admin/tables/${tableId}`,
        data: {
          name: editForm.name,
          version: editForm.version,
          description: editForm.description,
        },
      });

      setMessage({
        type: "success",
        text: "Table updated successfully.",
      });

      setEditingTableId(null);

      if (selectedCategory?.slug) {
        fetchTablesForCategory(selectedCategory);
      }
    } catch (err) {
      console.error("Error updating table from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error updating table.",
      });
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm("Are you sure you want to delete this table?")) {
      return;
    }

    try {
      await authorizedRequest({
        method: "delete",
        url: `/api/admin/delete-table/${tableId}`,
      });

      setMessage({
        type: "success",
        text: "Table deleted successfully.",
      });

      setTables((prev) => prev.filter((t) => t._id !== tableId));
    } catch (err) {
      console.error("Error deleting table from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error deleting table.",
      });
    }
  };

  const handleDownloadTable = async (tableId, name, version) => {
    try {
      const res = await authorizedRequest({
        method: "get",
        url: `/api/admin/download-table/${tableId}`,
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${name || "table"}-v${version || "1.0"}.CT`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading table from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: "Failed to download the table.",
      });
    }
  };

  // --- Requests tab: fetch requests when tab is opened ---

  const fetchDevRequests = async () => {
    try {
      setLoadingRequests(true);
      setRequestsError(null);

      const res = await authorizedRequest({
        method: "get",
        url: "/api/requests",
      });

      let requests = Array.isArray(res.data) ? res.data : [];

      // Sort newest -> oldest by createdAt
      requests = [...requests].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime; // newest first
      });

      setDevRequests(requests);
    } catch (err) {
      console.error("Error fetching requests for DeveloperPanel:", err);
      setRequestsError(
        err.response?.data?.message || "Error loading game requests."
      );
      setDevRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const approveRequest = async (requestId) => {
    try {
      setRequestActionLoading((prev) => ({ ...prev, [requestId]: true }));
      await authorizedRequest({
        method: "post",
        url: `/api/admin/approve/${requestId}`,
      });

      setMessage({
        type: "success",
        text: "Request accepted and marked as in progress.",
      });

      // Refresh list
      fetchDevRequests();
    } catch (err) {
      console.error("Error approving request from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error accepting request.",
      });
    } finally {
      setRequestActionLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const completeRequest = async (requestId) => {
    try {
      setRequestActionLoading((prev) => ({ ...prev, [requestId]: true }));
      await authorizedRequest({
        method: "post",
        url: `/api/admin/complete/${requestId}`,
      });

      setMessage({
        type: "success",
        text: "Request marked as completed.",
      });

      fetchDevRequests();
    } catch (err) {
      console.error("Error completing request from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error marking request as completed.",
      });
    } finally {
      setRequestActionLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      const rejectionReason = window.prompt(
        "Please enter a reason for rejecting this request:"
      );

      if (!rejectionReason) {
        // User cancelled or left empty
        return;
      }

      setRequestActionLoading((prev) => ({ ...prev, [requestId]: true }));

      await authorizedRequest({
        method: "post",
        url: `/api/admin/reject/${requestId}`,
        data: { rejectionReason },
      });

      setMessage({
        type: "success",
        text: "Request rejected successfully.",
      });

      fetchDevRequests();
    } catch (err) {
      console.error("Error rejecting request from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error rejecting request.",
      });
    } finally {
      setRequestActionLoading((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  useEffect(() => {
    if (activeTab === "requests") {
      fetchDevRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredRequests = useMemo(() => {
    let list = devRequests || [];

    if (requestStatusFilter !== "all") {
      list = list.filter(
        (r) =>
          (r.status || "").toLowerCase() ===
          requestStatusFilter.toLowerCase()
      );
    }

    const search = requestSearch.trim().toLowerCase();
    if (!search) return list;

    return list.filter((r) => {
      const title =
        (r.gameTitle || r.gameName || r.title || "").toLowerCase();
      const notes =
        (r.details || r.description || r.notes || "").toLowerCase();
      const platform = (r.platform || "").toLowerCase();
      const username =
        (r.username || r.userName || "").toLowerCase();

      return (
        title.includes(search) ||
        notes.includes(search) ||
        platform.includes(search) ||
        username.includes(search)
      );
    });
  }, [devRequests, requestStatusFilter, requestSearch]);

  // --- Reports tab: mark report resolved ---

  const resolveReports = async (categoryId) => {
    if (!categoryId) return;

    try {
      setReportActionLoading((prev) => ({ ...prev, [categoryId]: true }));

      await authorizedRequest({
        method: "post",
        url: `/api/admin/reports/${categoryId}/resolve`,
      });

      setMessage({
        type: "success",
        text: "Reports resolved successfully.",
      });

      fetchDevReports(); // Refresh reports
    } catch (err) {
      console.error("Error resolving reports from DeveloperPanel:", err);
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Error resolving reports.",
      });
    } finally {
      setReportActionLoading((prev) => ({ ...prev, [categoryId]: false }));
    }
  };

  // --- Reports tab: fetch reports when tab is opened ---

  const fetchDevReports = async () => {
    try {
      setLoadingReports(true);
      setReportsError(null);

      const res = await authorizedRequest({
        method: "get",
        url: "/api/admin/reports",
      });

      let reports = Array.isArray(res.data) ? res.data : [];

      // Sort highest reportCount first
      reports = [...reports].sort((a, b) => {
        const aCount = a.reportCount || 0;
        const bCount = b.reportCount || 0;
        return bCount - aCount;
      });

      setDevReports(reports);
    } catch (err) {
      console.error("Error fetching reports for DeveloperPanel:", err);
      setReportsError(
        err.response?.data?.message || "Error loading category reports."
      );
      setDevReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (activeTab === "reports") {
      fetchDevReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredReports = useMemo(() => {
    let list = devReports || [];

    if (reportsStatusFilter !== "all") {
      list = list.filter(
        (c) =>
          (c.status || "green").toLowerCase() ===
          reportsStatusFilter.toLowerCase()
      );
    }

    const search = reportsSearch.trim().toLowerCase();
    if (!search) return list;

    return list.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const slug = (c.slug || "").toLowerCase();

      const matchCategory =
        name.includes(search) || slug.includes(search);

      if (matchCategory) return true;

      const reportsArray = c.reports || [];
      return reportsArray.some((r) => {
        const desc = (r.description || "").toLowerCase();
        const funcs = (r.functionsAffected || "").toLowerCase();
        const errs = (r.errors || "").toLowerCase();
        const steps = (r.reproductionSteps || "").toLowerCase();

        return (
          desc.includes(search) ||
          funcs.includes(search) ||
          errs.includes(search) ||
          steps.includes(search)
        );
      });
    });
  }, [devReports, reportsStatusFilter, reportsSearch]);

  // --- Render ---

  if (checkingAuth) {
    return <p className="dev-panel-loading">Checking access...</p>;
  }

  if (unauthorized) {
    return (
      <div className="dev-panel-unauthorized">
        <h2>Access Restricted</h2>
        <p>This page is only available to Admins and Developers.</p>
        <button onClick={() => navigate("/")}>Back to Home</button>
      </div>
    );
  }

  return (
    <div className="dev-panel-page">
      <Helmet>
        <title>Developer Panel - Sintrix</title>
      </Helmet>

      <div className="dev-panel-card">
        <div className="dev-panel-header">
          <div>
            <h1>Developer Panel</h1>
            {user && (
              <p className="dev-panel-subtitle">
                Logged in as{" "}
                <strong>
                  {user.username} ({user.role})
                </strong>
              </p>
            )}
          </div>

          {/* Tabs */}
          <div className="dev-panel-tabs">
            <button
              type="button"
              className={
                activeTab === "tables"
                  ? "dev-panel-tab active"
                  : "dev-panel-tab"
              }
              onClick={() => setActiveTab("tables")}
            >
              Games &amp; Tables
            </button>
            <button
              type="button"
              className={
                activeTab === "requests"
                  ? "dev-panel-tab active"
                  : "dev-panel-tab"
              }
              onClick={() => setActiveTab("requests")}
            >
              Game Requests
            </button>
            <button
              type="button"
              className={
                activeTab === "reports"
                  ? "dev-panel-tab active"
                  : "dev-panel-tab"
              }
              onClick={() => setActiveTab("reports")}
            >
              Reports
            </button>
          </div>
        </div>

        {message && (
          <div
            className={`dev-panel-alert ${
              message.type === "error"
                ? "dev-panel-alert-error"
                : "dev-panel-alert-success"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* TAB: Games & Tables */}
        {activeTab === "tables" && (
          <div className="dev-panel-layout">
            {/* LEFT: Category + Upload */}
            <div className="dev-panel-column-upload">
              <h2 className="dev-panel-section-title">
                Game &amp; Table Management
              </h2>

              {/* Category selector + inline create */}
              <div className="dev-panel-field">
                <div className="dev-panel-field-label-row">
                  <label>Game Category</label>
                  <button
                    type="button"
                    className="dev-panel-inline-button"
                    onClick={() => setShowCategoryForm((prev) => !prev)}
                  >
                    {showCategoryForm ? "Close" : "New Category"}
                  </button>
                </div>

                {/* Hide game selector while creating a new category */}
                {!showCategoryForm && (
                  <>
                    <Select
                      options={categories.map((c) => ({
                        value: c.slug,
                        label: c.name,
                        raw: c,
                      }))}
                      onChange={(opt) => setSelectedCategory(opt?.raw || null)}
                      placeholder="Select a game..."
                      classNamePrefix="dev-panel-select"
                    />

                    {selectedCategory && (
                      <>
                        <p className="dev-panel-selected-category-hint">
                          Selected: <strong>{selectedCategory.name}</strong>
                        </p>

                        {/* NEW: Category image editor */}
                        <div className="dev-panel-category-image-editor">
                          <div className="dev-panel-category-field full">
                            <span>Category Image URL</span>
                            <input
                              type="text"
                              value={categoryImageUrl}
                              onChange={(e) => setCategoryImageUrl(e.target.value)}
                              placeholder="https://..."
                            />
                          </div>

                          <div className="dev-panel-category-actions">
                            <button
                              type="button"
                              className="dev-panel-btn"
                              disabled={savingCategoryImage}
                              onClick={async () => {
                                if (!selectedCategory?.slug) return;

                                try {
                                  setSavingCategoryImage(true);

                                  const url = (categoryImageUrl || "").trim();
                                  const res = await authorizedRequest({
                                    method: "put",
                                    url: `/api/admin/categories/${selectedCategory.slug}`,
                                    data: { images: url ? [url] : [] },
                                  });

                                  const updated = res.data?.category || res.data;

                                  setSelectedCategory(updated);
                                  setCategories((prev) =>
                                    prev.map((c) =>
                                      c.slug === updated.slug ? updated : c
                                    )
                                  );

                                  setMessage({
                                    type: "success",
                                    text: "Category image updated.",
                                  });
                                } catch (err) {
                                  console.error(
                                    "Error updating category image:",
                                    err
                                  );
                                  setMessage({
                                    type: "error",
                                    text:
                                      err.response?.data?.message ||
                                      "Failed to update category image.",
                                  });
                                } finally {
                                  setSavingCategoryImage(false);
                                }
                              }}
                            >
                              {savingCategoryImage ? "Saving..." : "Save Image"}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* New Category card (with optional first table) */}
                {showCategoryForm && (
                  <form
                    className="dev-panel-category-form"
                    onSubmit={handleCreateCategory}
                  >
                    {/* Steam Search + AppID */}
                    <div className="dev-panel-category-field full">
                      <span>Steam Game Search</span>
                      <div className="dev-panel-steam-search-row">
                        <input
                          type="text"
                          value={steamSearchQuery}
                          onChange={(e) => setSteamSearchQuery(e.target.value)}
                          placeholder="Search Steam for this game..."
                        />
                        <button
                          type="button"
                          className="dev-panel-btn secondary"
                          onClick={handleSteamSearch}
                          disabled={loadingSteam}
                        >
                          {loadingSteam ? "Searching..." : "Search"}
                        </button>
                      </div>
                      {steamResults.length > 0 && (
                        <div className="dev-panel-steam-results">
                          {steamResults.map((result) => (
                            <div
                              key={result.appid || result.url || result.name}
                              className="dev-panel-steam-result-item"
                              onClick={() => handleSteamResultClick(result)}
                            >
                              {result.image && (
                                <img
                                  src={result.image}
                                  alt={result.name}
                                  className="dev-panel-steam-result-image"
                                />
                              )}
                              <div className="dev-panel-steam-result-meta">
                                <div className="dev-panel-steam-result-name">
                                  {result.name}
                                </div>
                                {result.url && (
                                  <div className="dev-panel-steam-result-url">
                                    {result.url}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="dev-panel-category-row">
                      <div className="dev-panel-category-field">
                        <span>Steam AppID</span>
                        <input
                          type="text"
                          value={newCategory.appid}
                          onChange={(e) =>
                            handleNewCategoryChange("appid", e.target.value)
                          }
                          placeholder="Select from search above or paste URL / ID"
                        />
                      </div>
                    </div>

                    {/* NEW: Image URL (for creation) */}
                    <div className="dev-panel-category-row">
                      <div className="dev-panel-category-field full">
                        <span>Table Image URL (optional)</span>
                        <input
                          type="text"
                          value={newCategory.imageUrl}
                          onChange={(e) =>
                            handleNewCategoryChange("imageUrl", e.target.value)
                          }
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    {/* Category fields */}
                    <div className="dev-panel-category-row">
                      <div className="dev-panel-category-field">
                        <span>Name</span>
                        <input
                          type="text"
                          value={newCategory.name}
                          onChange={(e) =>
                            handleNewCategoryChange("name", e.target.value)
                          }
                          placeholder="e.g., Supermarket Together"
                        />
                      </div>
                      <div className="dev-panel-category-field">
                        <span>Slug</span>
                        <input
                          type="text"
                          value={newCategory.slug}
                          onChange={(e) =>
                            handleNewCategoryChange("slug", e.target.value)
                          }
                          placeholder="Leave empty! [Auto Generated]"
                        />
                      </div>
                    </div>

                    {/* Optional FIRST TABLE block */}
                    <div className="dev-panel-category-first-table">
                      <div className="dev-panel-category-first-table-header">
                        <span>First table (optional)</span>
                        <span className="dev-panel-category-first-table-tag"></span>
                      </div>

                      <div className="dev-panel-category-field full">
                        <span>Table File (.ct)</span>
                        <input
                          type="file"
                          accept=".ct"
                          onChange={handleNewCatTableFileChange}
                        />
                        {newCategoryFirstTable.file && (
                          <span className="dev-panel-first-table-file-name">
                            Selected: {newCategoryFirstTable.file.name}
                          </span>
                        )}
                      </div>

                      <div className="dev-panel-category-row">
                        <div className="dev-panel-category-field">
                          <span>Table Name</span>
                          <input
                            type="text"
                            value={newCategoryFirstTable.name}
                            onChange={(e) =>
                              handleNewCatTableChange("name", e.target.value)
                            }
                            placeholder="e.g., God Mode + XP"
                          />
                        </div>
                        <div className="dev-panel-category-field">
                          <span>Version</span>
                          <input
                            type="text"
                            value={newCategoryFirstTable.version}
                            onChange={(e) =>
                              handleNewCatTableChange("version", e.target.value)
                            }
                            placeholder="e.g., 1.0"
                          />
                        </div>
                      </div>

                      <div className="dev-panel-category-field full">
                        <span>Description (optional)</span>
                        <textarea
                          rows={2}
                          value={newCategoryFirstTable.description}
                          onChange={(e) =>
                            handleNewCatTableChange("description", e.target.value)
                          }
                          placeholder="What this first table does / notes."
                        />
                      </div>
                    </div>

                    <div className="dev-panel-category-actions">
                      <button
                        type="submit"
                        className="dev-panel-btn"
                        disabled={creatingCategory}
                      >
                        {creatingCategory ? "Creating..." : "Create Category"}
                      </button>
                      <button
                        type="button"
                        className="dev-panel-btn secondary"
                        onClick={() => {
                          setShowCategoryForm(false);
                          setNewCategory({
                            name: "",
                            slug: "",
                            appid: "",
                            imageUrl: "", // NEW
                          });
                          setNewCategoryFirstTable({
                            file: null,
                            name: "",
                            version: "",
                            description: "",
                          });
                          setSteamSearchQuery("");
                          setSteamResults([]);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Upload table for existing category – hidden if creating category */}
              {!showCategoryForm && (
                <form className="dev-panel-form" onSubmit={handleSubmit}>
                  <h3 className="dev-panel-subsection-title">Upload New Table</h3>

                  <div className="dev-panel-field">
                    <label>Table File (.ct)</label>
                    <div
                      className={`dev-panel-dropzone ${tableFile ? "has-file" : ""}`}
                      onDragOver={handleDragOver}
                      onDrop={handleFileDrop}
                    >
                      <p>
                        Drag &amp; drop a <code>.ct</code> file here, or click to
                        browse.
                      </p>
                      <input type="file" accept=".ct" onChange={handleFileChange} />
                      {tableFile && (
                        <p className="dev-panel-file-name">
                          Selected: {tableFile.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="dev-panel-field">
                    <label>Table Name</label>
                    <input
                      type="text"
                      value={tableName}
                      onChange={(e) => setTableName(e.target.value)}
                      placeholder="e.g., God Mode + XP Multiplier"
                    />
                  </div>

                  <div className="dev-panel-field">
                    <label>Version</label>
                    <input
                      type="text"
                      value={tableVersion}
                      onChange={(e) => setTableVersion(e.target.value)}
                      placeholder="e.g., 1.0, 1.1, 2.0"
                    />
                  </div>

                  <div className="dev-panel-field">
                    <label>Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Briefly describe what this table does and any important notes. Be sure to sign '--CamoHax'"
                      rows={4}
                    />
                  </div>

                  <button
                    type="submit"
                    className="dev-panel-submit"
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload Table"}
                  </button>
                </form>
              )}
            </div>


            {/* RIGHT: Manage tables */}
            <div className="dev-panel-column-manage">
              <h2 className="dev-panel-section-title">
                Manage Existing Tables
              </h2>
              {!selectedCategory && (
                <p className="dev-panel-hint">
                  Select a game on the left to view its tables.
                </p>
              )}

              {selectedCategory && (
                <>
                  {tablesLoading ? (
                    <p className="dev-panel-loading">Loading tables...</p>
                  ) : tables.length === 0 ? (
                    <p className="dev-panel-hint">
                      No tables found for{" "}
                      <strong>{selectedCategory.name}</strong>.
                    </p>
                  ) : (
                    <div className="dev-panel-tables-list">
                      {tables.map((table) => {
                        const isEditing = editingTableId === table._id;
                        return (
                          <div
                            key={table._id}
                            className="dev-panel-table-item"
                          >
                            <div className="dev-panel-table-main">
                              {isEditing ? (
                                <>
                                  <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) =>
                                      handleEditChange(
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    className="dev-panel-table-input"
                                    placeholder="Table name"
                                  />
                                  <input
                                    type="text"
                                    value={editForm.version}
                                    onChange={(e) =>
                                      handleEditChange(
                                        "version",
                                        e.target.value
                                      )
                                    }
                                    className="dev-panel-table-input small"
                                    placeholder="Version"
                                  />
                                  <textarea
                                    value={editForm.description}
                                    onChange={(e) =>
                                      handleEditChange(
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    className="dev-panel-table-textarea"
                                    placeholder="Description"
                                    rows={2}
                                  />
                                </>
                              ) : (
                                <>
                                  <div className="dev-panel-table-title-row">
                                    <span className="dev-panel-table-name">
                                      {table.name}
                                    </span>
                                    <span className="dev-panel-table-version">
                                      v{table.version}
                                    </span>
                                  </div>
                                  <p className="dev-panel-table-description">
                                    {table.description ||
                                      "No description provided."}
                                  </p>
                                  <div className="dev-panel-table-meta">
                                    <span>
                                      Uploaded:{" "}
                                      {table.createdAt
                                        ? new Date(
                                            table.createdAt
                                          ).toLocaleString()
                                        : "N/A"}
                                    </span>
                                    {table.updatedAt && (
                                      <span>
                                        Updated:{" "}
                                        {new Date(
                                          table.updatedAt
                                        ).toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="dev-panel-table-actions">
                              {!isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className="dev-panel-btn secondary"
                                    onClick={() =>
                                      handleDownloadTable(
                                        table._id,
                                        table.name,
                                        table.version
                                      )
                                    }
                                  >
                                    Download
                                  </button>
                                  <button
                                    type="button"
                                    className="dev-panel-btn"
                                    onClick={() => startEditTable(table)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="dev-panel-btn danger"
                                    onClick={() =>
                                      handleDeleteTable(table._id)
                                    }
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="dev-panel-btn"
                                    onClick={() =>
                                      handleUpdateTable(table._id)
                                    }
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="dev-panel-btn secondary"
                                    onClick={cancelEditTable}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* TAB: Game Requests */}
        {activeTab === "requests" && (
          <div className="dev-panel-requests-wrapper">
            <div className="dev-panel-requests-header">
              <h2 className="dev-panel-section-title">Game Requests</h2>
              <p className="dev-panel-hint">
                View what users are asking for so you can plan new tables.
              </p>
            </div>

            <div className="dev-panel-requests-filters">
              <select
                value={requestStatusFilter}
                onChange={(e) => setRequestStatusFilter(e.target.value)}
                className="dev-panel-requests-status-select"
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>

              <input
                type="text"
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
                placeholder="Search by game, platform, notes, user..."
                className="dev-panel-requests-search"
              />

              <button
                type="button"
                className="dev-panel-btn secondary"
                onClick={fetchDevRequests}
                disabled={loadingRequests}
              >
                {loadingRequests ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {requestsError && (
              <div className="dev-panel-alert dev-panel-alert-error">
                {requestsError}
              </div>
            )}

            {loadingRequests && !devRequests.length ? (
              <p className="dev-panel-loading">Loading game requests...</p>
            ) : filteredRequests.length === 0 ? (
              <p className="dev-panel-hint">
                No requests found with the current filters.
              </p>
            ) : (
              <div className="dev-panel-requests-list">
                {filteredRequests.map((req) => (
                  <div
                    key={req._id}
                    className="dev-panel-request-card"
                  >
                    <div className="dev-panel-request-header">
                      <div>
                        <h3 className="dev-panel-request-title">
                          {req.steamGameDetails?.name ||
                            req.gameName ||
                            req.title ||
                            "Untitled Request"}
                        </h3>
                        <div className="dev-panel-request-sub">
                          {req.platform && (
                            <span>{req.platform}</span>
                          )}
                          {req.status && (
                            <span className={`status-pill status-${String(req.status).toLowerCase()}`}>
                              {req.status}
                            </span>
                          )}
                        </div>
                      </div>
                      {typeof req.votes === "number" && (
                        <div className="dev-panel-request-votes">
                          <span className="dev-panel-request-votes-count">
                            {req.votes}
                          </span>
                          <span className="dev-panel-request-votes-label">
                            votes
                          </span>
                        </div>
                      )}
                    </div>

                    {(req.details || req.description || req.notes) && (
                      <p className="dev-panel-request-body">
                        {req.details || req.description || req.notes}
                      </p>
                    )}

                    {req.cheatDetails && (
                      <p className="dev-panel-request-body">
                        <strong>Cheat Details:</strong> {req.cheatDetails}
                      </p>
                    )}

                    {String(req.status).toLowerCase() === "rejected" &&
                      req.rejectionReason && (
                        <p className="dev-panel-request-body dev-panel-rejection-reason">
                          <strong>Rejection reason:</strong> {req.rejectionReason}
                        </p>
                      )}
                    <div className="dev-panel-request-footer">
                      {req.username && (
                        <span>
                          By{" "}
                          <a
                            href={`/profile/${req.username}`}
                            className="dev-panel-profile-link"
                          >
                            {req.username}
                          </a>
                        </span>
                      )}

                      {req.createdAt && (
                        <span>{new Date(req.createdAt).toLocaleString()}</span>
                      )}
                    </div>

                    {/* Dev actions */}
                    <div className="dev-panel-request-actions">
                      {/* Accept / start working – only if still pending */}
                      {String(req.status).toLowerCase() === "pending" && (
                        <>
                          <button
                            type="button"
                            className="dev-panel-btn"
                            disabled={!!requestActionLoading[req._id]}
                            onClick={() => approveRequest(req._id)}
                          >
                            {requestActionLoading[req._id] ? "Accepting..." : "Accept"}
                          </button>

                          <button
                            type="button"
                            className="dev-panel-btn danger"
                            disabled={!!requestActionLoading[req._id]}
                            onClick={() => rejectRequest(req._id)}
                          >
                            {requestActionLoading[req._id] ? "Rejecting..." : "Reject"}
                          </button>
                        </>
                      )}

                      {/* Mark completed – visible for anything not already completed/rejected */}
                      {["completed", "rejected"].includes(
                        String(req.status).toLowerCase()
                      ) ? null : (
                        <button
                          type="button"
                          className="dev-panel-btn secondary"
                          disabled={!!requestActionLoading[req._id]}
                          onClick={() => completeRequest(req._id)}
                        >
                          {requestActionLoading[req._id] ? "Saving..." : "Mark Completed"}
                        </button>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: Reports */}
        {activeTab === "reports" && (
          <div className="dev-panel-requests-wrapper">
            <div className="dev-panel-requests-header">
              <h2 className="dev-panel-section-title">Category Reports</h2>
              <p className="dev-panel-hint">
                See which games are being reported as broken or having issues.
              </p>
            </div>

            <div className="dev-panel-requests-filters">
              <select
                value={reportsStatusFilter}
                onChange={(e) => setReportsStatusFilter(e.target.value)}
                className="dev-panel-requests-status-select"
              >
                <option value="all">All statuses</option>
                <option value="green">Working (Green)</option>
                <option value="yellow">Possible Issues (Yellow)</option>
                <option value="red">Broken (Red)</option>
              </select>

              <input
                type="text"
                value={reportsSearch}
                onChange={(e) => setReportsSearch(e.target.value)}
                placeholder="Search by game, slug, or report text..."
                className="dev-panel-requests-search"
              />

              <button
                type="button"
                className="dev-panel-btn secondary"
                onClick={fetchDevReports}
                disabled={loadingReports}
              >
                {loadingReports ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {reportsError && (
              <div className="dev-panel-alert dev-panel-alert-error">
                {reportsError}
              </div>
            )}

            {loadingReports && !devReports.length ? (
              <p className="dev-panel-loading">Loading reports...</p>
            ) : filteredReports.length === 0 ? (
              <p className="dev-panel-hint">
                No reports found with the current filters.
              </p>
            ) : (
              <div className="dev-panel-requests-list">
                {filteredReports.map((cat) => {
                  const categoryKey = cat._id || cat.categoryId || cat.slug;

                  return (
                    <div
                      key={categoryKey}
                      className="dev-panel-request-card"
                    >
                      <div className="dev-panel-request-header">
                        <div>
                          <h3 className="dev-panel-request-title">
                            {cat.name || "Unnamed Category"}
                          </h3>
                          <div className="dev-panel-request-sub">
                            {cat.status && (() => {
                              const rawStatus = String(cat.status).toLowerCase();
                              const prettyLabel =
                                REPORT_STATUS_LABELS[rawStatus] || cat.status;

                              return (
                                <span
                                  className={`status-pill status-${rawStatus}`}
                                >
                                  {prettyLabel}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="dev-panel-request-votes">
                          <span className="dev-panel-request-votes-count">
                            {cat.reportCount || (cat.reports || []).length || 0}
                          </span>
                          <span className="dev-panel-request-votes-label">
                            reports
                          </span>
                        </div>
                      </div>

                      {/* Category-level resolve button */}
                      <div className="dev-panel-request-actions dev-panel-report-actions">
                        <button
                          type="button"
                          className="dev-panel-btn secondary"
                          disabled={!!reportActionLoading?.[categoryKey]}
                          onClick={() => resolveReports(categoryKey)}
                        >
                          {reportActionLoading?.[categoryKey]
                            ? "Saving..."
                            : "Mark Reports Resolved"}
                        </button>
                      </div>

                      {/* Report list */}
                      {(cat.reports || []).length === 0 ? (
                        <p className="dev-panel-request-body">
                          No detailed reports stored for this category.
                        </p>
                      ) : (
                        <div className="dev-panel-report-items">
                          {cat.reports.map((rep, idx) => (
                            <div
                              key={rep._id || idx}
                              className="dev-panel-report-item"
                            >
                              <div className="dev-panel-report-meta">
                                <span>
                                  <strong>Version:</strong>{" "}
                                  {rep.tableVersion || "Unknown"}
                                </span>

                                {rep.reportedAt && (
                                  <span>
                                    <strong>Reported:</strong>{" "}
                                    {new Date(rep.reportedAt).toLocaleString()}
                                  </span>
                                )}

                                {(() => {
                                  const reporterName =
                                    rep.reporterUsername ||
                                    rep.userId?.username ||
                                    rep.userId?.email ||
                                    null;

                                  if (!reporterName) return null;

                                  return (
                                    <span>
                                      <strong>By:</strong>{" "}
                                      <a
                                        href={`/profile/${reporterName}`}
                                        className="dev-panel-profile-link"
                                      >
                                        {reporterName}
                                      </a>
                                    </span>
                                  );
                                })()}

                                {rep.resolved && (
                                  <span>
                                    <strong>Status:</strong> Resolved
                                    {rep.resolvedAt
                                      ? ` (${new Date(
                                          rep.resolvedAt
                                        ).toLocaleString()})`
                                      : ""}
                                  </span>
                                )}
                              </div>

                              {rep.description && (
                                <p className="dev-panel-request-body">
                                  <strong>Summary:</strong> {rep.description}
                                </p>
                              )}

                              {rep.functionsAffected && (
                                <p className="dev-panel-request-body">
                                  <strong>Functions Not Working:</strong>{" "}
                                  {rep.functionsAffected}
                                </p>
                              )}

                              {rep.errors && (
                                <p className="dev-panel-request-body">
                                  <strong>Errors / Logs:</strong>{" "}
                                  {rep.errors}
                                </p>
                              )}

                              {rep.reproductionSteps && (
                                <p className="dev-panel-request-body">
                                  <strong>Steps to Reproduce:</strong>{" "}
                                  {rep.reproductionSteps}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeveloperPanel;
